import React from 'react';
import { motion } from 'framer-motion';
import { 
  Ship, Plane, Truck, Package, ShieldCheck, Globe, Clock, Anchor, 
  MapPin, Phone, Mail, CheckCircle2, ChevronRight, Menu, X,
  Factory, Tractor, Cpu, FlaskConical, Stethoscope, Scissors, Zap
} from 'lucide-react';
import './home.css';
import Header from '../../components/header/header';
import Footer from '../../components/footer/footer';
import Slider from '../../components/slider/slider';

const Home = () => {

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  return (
    <div className="lothal-app">
      {/* Header */}
      <Header />

      {/* Hero Section */}
      <section id="home" className="hero">
        <div className="hero-bg">
          <img src="/hero_bg.png" alt="Cargo Ship at Port" onError={(e) => e.target.src = "https://images.unsplash.com/photo-1494412519320-aa613dfb7738?auto=format&fit=crop&q=80&w=2070"} />
        </div>
        <div className="hero-overlay"></div>
        <div className="container">
          <motion.div 
            className="hero-content"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.h1 className="hero-title" variants={fadeInUp}>
              Connecting Businesses Across Borders with <span>Smart Logistics</span>
            </motion.h1>
            <motion.p className="hero-subtitle" variants={fadeInUp}>
              Reliable freight forwarding, customs clearance, air cargo, sea cargo, and supply chain solutions designed to move your business faster and safer.
            </motion.p>
            <motion.div className="hero-buttons" variants={fadeInUp}>
              <button className="btn btn-primary btn-lg">Request Quote</button>
              <button className="btn btn-outline btn-lg">Explore Services</button>
            </motion.div>
            <motion.div className="hero-trust" variants={fadeInUp}>
              <CheckCircle2 className="trust-icon" size={20} />
              <span>Serving businesses across India & worldwide with efficient cargo movement.</span>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="section-padding">
        <div className="container">
          <div className="about-grid">
            <motion.div 
              className="about-image"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&q=80&w=2070" alt="Warehouse Logistics" />
              <div className="about-experience">
                <h3>15+</h3>
                <p>Years Experience</p>
              </div>
            </motion.div>
            
            <motion.div 
              className="about-content"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2>Your Trusted Global Logistics Partner</h2>
              <p>At Lothal International, we simplify international trade through efficient logistics planning, secure transportation, and seamless customs support. From urgent air freight to large-scale ocean cargo, we deliver tailored solutions that keep businesses moving.</p>
              <p>With a commitment to speed, transparency, and customer satisfaction, we help importers, exporters, manufacturers, and traders reach global markets confidently.</p>
              
              <div className="about-features">
                <div className="feature-item">
                  <Clock className="feature-icon" /> Speed & Efficiency
                </div>
                <div className="feature-item">
                  <ShieldCheck className="feature-icon" /> Secure Transport
                </div>
                <div className="feature-item">
                  <Globe className="feature-icon" /> Global Network
                </div>
                <div className="feature-item">
                  <CheckCircle2 className="feature-icon" /> Customs Support
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="services section-padding">
        <div className="container">
          <h2 className="section-title">Complete Logistics Services <span>Under One Roof</span></h2>
          
          <motion.div 
            className="services-grid"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {[
              { icon: <Plane size={32}/>, title: "Air Freight", desc: "Fast and secure worldwide air cargo for urgent shipments." },
              { icon: <Ship size={32}/>, title: "Ocean Freight", desc: "Cost-effective FCL & LCL shipping solutions worldwide." },
              { icon: <Anchor size={32}/>, title: "Customs Clearance", desc: "Smooth import/export documentation and clearance support." },
              { icon: <Truck size={32}/>, title: "Road Transport", desc: "Domestic movement with timely and safe delivery." },
              { icon: <Package size={32}/>, title: "Warehousing", desc: "Smart storage and inventory handling solutions." },
              { icon: <Factory size={32}/>, title: "Project Cargo", desc: "Specialized handling for oversized, heavy, and industrial cargo." },
              { icon: <Zap size={32}/>, title: "Hazardous Cargo", desc: "Safe handling of regulated and sensitive goods." },
              { icon: <Globe size={32}/>, title: "Cross Border Logistics", desc: "Reliable cargo movement across international markets." }
            ].map((srv, i) => (
              <motion.div className="service-card" key={i} variants={fadeInUp}>
                <div className="service-icon">{srv.icon}</div>
                <h3>{srv.title}</h3>
                <p>{srv.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="why-us section-padding">
        <div className="container">
          <h2 className="section-title" style={{color: 'white'}}>Why Businesses Trust <span>Lothal International</span></h2>
          
          <motion.div 
            className="why-grid"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {[
              "Fast Turnaround Time", "Competitive Freight Rates", "Real-Time Shipment Updates",
              "Experienced Logistics Team", "Global Partner Network", "Safe & Secure Handling",
              "24/7 Customer Support", "Customized Supply Chain Solutions"
            ].map((item, i) => (
              <motion.div className="why-card" key={i} variants={fadeInUp}>
                <CheckCircle2 className="why-icon" size={36} />
                <h3>{item}</h3>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Industries */}
      <section id="industries" className="section-padding">
        <div className="container">
          <h2 className="section-title">Industries We <span>Power</span></h2>
          
          <motion.div 
            className="industries-grid"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {[
              { icon: <Factory />, name: "Steel & Metals" },
              { icon: <Scissors />, name: "Textile & Garments" },
              { icon: <Cpu />, name: "Machinery" },
              { icon: <FlaskConical />, name: "Chemicals" },
              { icon: <Zap />, name: "Electronics" },
              { icon: <Truck />, name: "Automotive" },
              { icon: <Package />, name: "FMCG" },
              { icon: <Stethoscope />, name: "Pharma" },
              { icon: <Tractor />, name: "Agriculture Products" }
            ].map((ind, i) => (
              <motion.div className="industry-tag" key={i} variants={fadeInUp}>
                <span className="industry-icon">{ind.icon}</span>
                {ind.name}
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="stats">
        <div className="container">
          <div className="stats-grid">
            {[
              { count: "5000+", label: "Shipments Managed" },
              { count: "50+", label: "Global Destinations" },
              { count: "100+", label: "Happy Clients" },
              { count: "24/7", label: "Customer Support" }
            ].map((stat, i) => (
              <div className="stat-item" key={i}>
                <h3>{stat.count}</h3>
                <p>{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Slider />

      {/* CTA */}
      <section className="cta">
        <div className="container">
          <motion.div 
            className="cta-content"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2>Ready to Ship Smarter?</h2>
            <p>Let our experts manage your cargo while you focus on growing your business.</p>
            <div className="cta-buttons">
              <button className="btn btn-primary btn-lg">Get Free Quote</button>
              <button className="btn btn-outline btn-lg">Contact Team</button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Home;
